<template>
  <div class="saas-listing-page">
    <div class="saas-page-head">
      <div class="saas-rounded-shape saas-rounded-shape__vertical-left-1"></div>
      <div class="saas-rounded-shape saas-rounded-shape__vertical-left-2"></div>
      <div class="saas-rounded-shape saas-rounded-shape__vertical-left-3"></div>
      <div
        class="saas-rounded-shape saas-rounded-shape__vertical-right-1"
      ></div>
      <div
        class="saas-rounded-shape saas-rounded-shape__vertical-right-2"
      ></div>
      <div
        class="saas-rounded-shape saas-rounded-shape__vertical-right-3"
      ></div>
      <h1 class="saas-page-head__title">
        Things you <strong>probably wonder</strong>
      </h1>
      <p class="saas-page-head__description">
        Also known as frequently asked questions
      </p>
      <TextSearchInput
        v-model="searchQuery"
        placeholder="Search questions..."
      ></TextSearchInput>
    </div>
    <div ref="body" class="saas-faq-listing-page__body">
      <div class="saas-faq-listing-page__sidebar">
        <div class="saas-faq-listing-page__sidebar-sticky">
          <div class="saas-faq-listing-page__sidebar-small-title">
            Categories
          </div>
          <ul class="saas-faq-listing-page__categories">
            <li
              v-for="category in categories"
              :key="category.id"
              class="saas-faq-listing-page__categories-item"
            >
              <a
                v-smooth-scroll
                :href="`#${category.name}`"
                class="saas-faq-listing-page__categories-link"
              >
                {{ category.name }}</a
              >
            </li>
          </ul>
        </div>
      </div>
      <div class="saas-faq-listing-page__faq">
        <template v-if="hasSearch">
          <div
            v-if="searchResults.length > 0"
            class="saas-faq-listing-page__faq-head"
          >
            <h2 class="saas-faq-listing-page__faq-title">
              Results for "{{ searchQuery }}"
            </h2>
            <Button type="ghost" @click="searchQuery = ''">Clear search</Button>
          </div>
          <div v-if="searchResults.length > 0" class="saas-faq-cards">
            <FAQCard
              v-for="(item, index) in searchResults"
              :key="'faq-' + index"
              :item="item"
              @toggle="toggleFAQItem(item, index)"
            ></FAQCard>
          </div>
          <div v-if="searchResults < 1" class="placeholder">
            <div class="placeholder__title">No questions found</div>
            <p class="placeholder__content">
              Check your spelling, or try refining your search.
            </p>
            <div class="placeholder__action">
              <Button size="large" type="ghost" @click="searchQuery = ''">
                Back to all questions
              </Button>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="category in categories"
            :key="category.id"
            class="saas-faq-listing-page__faq-group"
          >
            <h2 :id="category.name" class="saas-faq-listing-page__faq-title">
              {{ category.name }}
            </h2>
            <div v-if="category.items.length > 0" class="saas-faq-cards">
              <FAQCard
                v-for="(item, index) in category.items"
                :key="'faq-' + index"
                :item="item"
                @toggle="toggleFAQItem(item, index)"
              ></FAQCard>
            </div>
          </div>
        </template>
      </div>
    </div>
    <FooterJoin
      title="Still having **questions?**"
      text="Reach out via email or in the community."
      :buttons="[
        {
          id: '1',
          value: {
            type: 'primary',
            text: 'Contact us',
            url_or_path: '/contact',
          },
        },
        {
          id: '2',
          value: {
            type: 'ghost',
            text: 'Join the community',
            url_or_path: '/community',
          },
        },
      ]"
    ></FooterJoin>
  </div>
</template>

<script>
import FAQService from '@saas/services/faq'
import FAQCard from '@saas/components/FAQCard'
import FooterJoin from '@saas/components/footer/FooterJoin'
import TextSearchInput from '@saas/components/TextSearchInput'
import { escapeRegExp } from '@baserow/modules/core/utils/string'
import { clone } from '@baserow/modules/core/utils/object'

export default {
  components: { TextSearchInput, FAQCard, FooterJoin },
  layout: 'saas',
  async asyncData({ app }) {
    const { data } = await FAQService(app.$client).fetchAll()
    const categories = []
    data.forEach((item) => {
      item._ = { open: false }

      const category = categories.find(
        (category) => category.id === item.category.id
      )
      if (!category) {
        const categoryCopy = clone(item.category)
        categoryCopy.items = [item]
        categories.push(categoryCopy)
      } else {
        category.items.push(item)
      }
    })
    return { categories }
  },
  data() {
    return {
      searchQuery: '',
    }
  },
  head() {
    return {
      title: 'FAQ',
      meta: [
        {
          name: 'description',
          content:
            'Frequently asked question for the hosted and self hosted version of Baserow.',
        },
      ],
      bodyAttrs: {
        class: ['faq-page'],
      },
    }
  },
  computed: {
    hasSearch() {
      return this.searchQuery !== ''
    },
    searchResults() {
      if (!this.hasSearch) {
        return []
      }

      const items = []
      this.categories.forEach((category) => {
        category.items
          .filter((item) => {
            const regex = new RegExp(
              '(' + escapeRegExp(this.searchQuery) + ')',
              'i'
            )
            return (
              item.question.match(regex) ||
              item.answer.match(regex) ||
              item.search_keywords.match(regex)
            )
          })
          .forEach((item) => {
            items.push(item)
          })
      })

      return items
    },
  },
  methods: {
    toggleFAQItem(item, index) {
      item._.open = !item._.open
    },
  },
}
</script>
